import { routeLineOfBusiness } from "src/claimsHistory/registry";
import { LOB_ROLES } from "src/jsonForms/preScreen/lobAccess";

import { Role, User } from "../../auth/types/interfaces";
import { forbiddenResponse, successResponse } from "../constants";

/**
 * Loader function for React Router that runs when the user navigates to a claims
 * history route, deciding authorization before the page mounts.
 *
 * The roles that grant access come from `LOB_ROLES`, the single role registry, so
 * adding or removing a role for a line of business changes this decision with no
 * edit here.
 *
 * An empty role list is a denial, not a pending state: `createAppRouter` is
 * called with the parsed user only after authentication resolves, so by the time
 * a loader runs the roles are whatever the Cognito `profile` claim carried. No
 * roles means the user holds none, and saying "retry in a few moments" would
 * leave them retrying something that will not change.
 *
 * @param user - The signed-in user, including their roles.
 * @param pathname - The requested pathname, used to resolve the line of business.
 * @returns A 200 "OK" response when the user holds a role listed for the route's
 *   line of business.
 * @throws A 403 response when the user holds no roles at all, when the pathname
 *   matches no registered line of business, when the user holds no role listed
 *   for it, or when the user holds `wc-claim-user-limited`.
 */
export const loader = async (user: User, pathname: string): Promise<Response> => {
  const roles = user.roles;
  if (!roles?.length) {
    throw forbiddenResponse();
  }

  const lob = routeLineOfBusiness(pathname);
  if (!lob) {
    throw forbiddenResponse();
  }

  // `LOB_ROLES.wc` lists wc-claim-user-limited because that role grants claim
  // submission. Claims history denies it on every route, as it does today.
  if (roles.includes(Role.WC_CLAIM_USER_LIMITED)) {
    throw forbiddenResponse();
  }

  if (!LOB_ROLES[lob].some((role) => roles.includes(role))) {
    throw forbiddenResponse();
  }

  return successResponse();
};
